/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatePipe, DecimalPipe } from '@angular/common';
import { Inject, Injectable, LOCALE_ID, Optional } from '@angular/core';
import { isDate, isValidDate } from '../date';
import { CUSTOM_FORMATTERS, CustomFormatter } from './custom-formatter';
import { WithFormatted } from './with-formatted';

@Injectable({ providedIn: 'root' })
export class DataFormatterService {
  readonly #customFormatters: CustomFormatter[];
  readonly #decimalPipe: DecimalPipe;
  readonly #datePipe: DatePipe;
  readonly #defaultLocale: string;

  constructor(
    @Optional() @Inject(CUSTOM_FORMATTERS) customFormatters: CustomFormatter[],
    decimalPipe: DecimalPipe,
    datePipe: DatePipe,
    @Inject(LOCALE_ID) locale: string
  ) {
    this.#customFormatters = Array.isArray(customFormatters)
      ? customFormatters
      : [];
    this.#decimalPipe = decimalPipe;
    this.#datePipe = datePipe;
    this.#defaultLocale = locale;
  }

  formatFields<T>(
    data: T,
    numberFormat = '1.2-2',
    dateFormat = 'dd/MM/yyyy',
    locale = this.#defaultLocale
  ): WithFormatted<T> {
    return this._formatRecursive(
      data,
      numberFormat,
      dateFormat,
      locale
    ) as WithFormatted<T>;
  }

  private _formatRecursive(
    value: any,
    numberFormat = '1.2-2',
    dateFormat = 'dd/MM/yyyy',
    locale = this.#defaultLocale
  ): any {
    if (Array.isArray(value)) {
      return value.map((item) =>
        this._formatRecursive(item, numberFormat, dateFormat, locale)
      );
    }

    if (value && typeof value === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let result: any = { ...value };
      for (const key of Object.keys(value)) {
        const val = value[key];
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          result[key] = this._formatRecursive(
            val,
            numberFormat,
            dateFormat,
            locale
          );
          continue;
        }

        if (Array.isArray(val)) {
          result[key] = val.map((v) =>
            this._formatRecursive(v, numberFormat, dateFormat, locale)
          );
          continue;
        }

        let formatAsDate = false;
        if (
          typeof val === 'string' &&
          /(?:\d{4})-(?:\d{2})-(?:\d{2})T(?:\d{2}):(?:\d{2}):(?:\d{2}).(?:\d{3})Z/i.test(
            val
          )
        ) {
          formatAsDate = true;
        }

        if (isDate(value)) {
          formatAsDate = true;
        }

        if (formatAsDate && isValidDate(val)) {
          const formatted = this.#datePipe.transform(
            val,
            dateFormat,
            undefined,
            locale
          );
          if (formatted) {
            result[`${key}Formatted`] = formatted;
          }

          continue;
        }

        if (typeof val === 'number') {
          const formatted = this.#decimalPipe.transform(
            val,
            numberFormat,
            locale
          );
          if (formatted) {
            result[`${key}Formatted`] = formatted;
          }

          continue;
        }
      }

      // Aplicar formateadores personalizados si aplican
      for (const formatter of this.#customFormatters) {
        if (formatter.supports(value)) {
          const formatted = formatter.format(value);
          result = {
            ...result,
            ...formatted,
          };
        }
      }

      return result;
    }

    return value;
  }
}
